/**
 * ADR-118 — auth state reducer.
 *
 * Pure function. Given a current state + action, return the next
 * state. Side effects (storage writes, cookie clears, WS close)
 * live in the consuming layer (`libs/auth-identity`'s provider),
 * NOT here. Keeping the reducer pure means we can unit-test every
 * state transition in isolation under Vitest.
 *
 * Invalid transitions are silently no-op'd. The reducer returns the
 * incoming state unchanged rather than throwing — defensive against
 * stale dispatches during component-unmount races.
 */

import type { AuthAction, AuthState, BaseUser } from './types';

export function authReducer<U extends BaseUser>(
  state: AuthState<U>,
  action: AuthAction<U>,
): AuthState<U> {
  switch (action.type) {
    case 'login_start':
      if (state.status === 'anonymous' || state.status === 'logged-out') {
        return { status: 'authenticating' };
      }
      // Don't let a stray login_start clobber an authenticated state.
      return state;

    case 'login_success':
      return {
        status: 'authenticated',
        user: action.user,
        jwt: action.jwt,
        refreshToken: action.refreshToken,
      };

    case 'login_failure':
      if (state.status === 'authenticating') {
        return { status: 'anonymous' };
      }
      return state;

    case 'refresh_start':
      if (state.status === 'authenticated') {
        return { ...state, status: 'refreshing' };
      }
      return state;

    case 'refresh_success':
      if (state.status === 'refreshing') {
        return {
          ...state,
          status: 'authenticated',
          jwt: action.jwt,
          refreshToken: action.refreshToken ?? state.refreshToken,
        };
      }
      return state;

    case 'refresh_failure':
      if (state.status === 'refreshing') {
        return { status: 'expired', user: state.user };
      }
      return state;

    case 'expire':
      if (
        state.status === 'authenticated' ||
        state.status === 'refreshing'
      ) {
        return { status: 'expired', user: state.user };
      }
      return state;

    case 'logout':
      return { status: 'logged-out' };

    case 'hydrate':
      // Whole-state replacement. Used at boot once reconciliation
      // decides what state matches the world.
      return action.state;

    case 'set_user':
      // Replace `user` in place without touching token /
      // status. Used by `libs/auth-permissions` after a
      // permissions_version bump → /users/me/ refetch. Only valid
      // for states that already have a user; otherwise no-op.
      //
      // Bail out when the incoming user is deep-equal to
      // the current one. `set_user` arrives from speculative refresh
      // paths (poll fallback, window-focus, bursty WS frames) that
      // often re-fetch an unchanged `/users/me/`. Returning the SAME
      // state reference lets React's `useReducer` skip the re-render
      // entirely, which in turn keeps the provider's memoized
      // `actions` / context value stable — without this, every
      // no-op refresh would churn the AuthContext identity and
      // re-render every `useUser`/`useAuth` consumer app-wide
      // (plus re-persist the user JSON to localStorage, firing
      // cross-tab 'token-changed' rehydrates).
      if (
        (state.status === 'authenticated' ||
          state.status === 'refreshing' ||
          state.status === 'expired') &&
        jsonDeepEqual(state.user, action.user)
      ) {
        return state;
      }
      if (
        state.status === 'authenticated' ||
        state.status === 'refreshing'
      ) {
        return { ...state, user: action.user };
      }
      if (state.status === 'expired') {
        return { ...state, user: action.user };
      }
      // anonymous / authenticating / logged-out — no user field, so
      // dropping the action is the right answer. Caller racing a
      // logout is the typical reason.
      return state;

    default:
      // Exhaustiveness check — TS will flag if we forget a case.
      return assertNever(action);
  }
}

function assertNever(x: never): never {
  throw new Error(
    `[auth-core] reducer received unknown action: ${JSON.stringify(x)}`,
  );
}

/**
 * Structural equality for JSON-shaped values (the user
 * object arrives from a `/users/me/` JSON response: plain objects,
 * arrays, and primitives only).
 *
 * Module-private on purpose: this is a reducer implementation detail,
 * not lib API. No dependency (the repo carries no lodash /
 * fast-deep-equal), and deliberately CONSERVATIVE — anything exotic
 * (class instances, Dates, Maps, …) compares unequal unless
 * reference-identical, so the worst failure mode is a redundant
 * dispatch, never a swallowed real change. User objects are small
 * (tens of keys), so the recursive walk is cheap relative to the
 * app-wide re-render it prevents.
 */
function jsonDeepEqual(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;
  if (
    typeof a !== 'object' ||
    typeof b !== 'object' ||
    a === null ||
    b === null
  ) {
    return false;
  }
  const aIsArray = Array.isArray(a);
  const bIsArray = Array.isArray(b);
  if (aIsArray || bIsArray) {
    if (!aIsArray || !bIsArray) return false;
    if (a.length !== b.length) return false;
    return a.every((item, i) => jsonDeepEqual(item, b[i]));
  }
  if (!isPlainObject(a) || !isPlainObject(b)) {
    // Non-JSON shape (Date, Map, class instance, …) — treat as
    // changed rather than risk a false "equal".
    return false;
  }
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  return aKeys.every(
    (key) =>
      Object.prototype.hasOwnProperty.call(b, key) &&
      jsonDeepEqual(a[key], b[key]),
  );
}

function isPlainObject(x: object): x is Record<string, unknown> {
  const proto: unknown = Object.getPrototypeOf(x);
  return proto === Object.prototype || proto === null;
}
